import { Button, Stack } from '@mui/material';
import { DeepPartial } from 'react-hook-form';
import Iconify from 'src/components/iconify';
import { useTable } from 'src/components/table';
import { PropertyModel } from 'src/gql/graphql';
import {
  invalidateGetPropertySimilaritiesQueries,
  useGetPropertySimilaritiesQuery,
  useUpdateSimilaritiesPropertyMutation,
} from 'src/services/properties';
import { useBoolean } from 'src/hooks/use-boolean';
import { LoadingButton } from '@mui/lab';
import { xor } from 'lodash';
import { useMemo } from 'react';
import { PropertyTableListSmall } from '../property-table-small';
import PropertyListDialog from '../property-list-dialog';

type Props = {
  property: DeepPartial<PropertyModel>;
};

export default function PropertySimilaritiesView({ property }: Props) {
  const dialog = useBoolean();

  const { data, isLoading: isListLoading } = useGetPropertySimilaritiesQuery({
    id: property.id!,
  });

  const { mutateAsync, isPending } = useUpdateSimilaritiesPropertyMutation();

  const isLoading = isListLoading || isPending;

  const table = useTable({
    defaultOrderBy: '_id',
    defaultOrder: 'desc',
    defaultCurrentPage: 0,
    defaultRowsPerPage: 10,
  });

  const selected = useMemo(
    () => (data?.property.similarities ?? []).map((i) => i.id),
    [data]
  );

  const handleUpdate = async (itemId: string) => {
    await mutateAsync({
      id: property.id!,
      similarities: xor(selected, [itemId]),
    });

    await invalidateGetPropertySimilaritiesQueries({ id: property.id! });
  };

  const handleAdd = async (item: DeepPartial<PropertyModel> | null) => {
    handleUpdate(item?.id!);
    dialog.onFalse();
  };

  const handleTrash = (item: DeepPartial<PropertyModel> | null) => () =>
    handleUpdate(item?.id!);

  if (!data) {
    return null;
  }

  return (
    <Stack gap={5}>
      <Stack justifyContent="flex-end" direction="row" gap={2}>
        <LoadingButton
          size="medium"
          variant="contained"
          startIcon={<Iconify icon="mingcute:add-line" />}
          onClick={dialog.onTrue}
          loading={isLoading}
        >
          Add Property
        </LoadingButton>
      </Stack>

      <PropertyTableListSmall
        table={table}
        items={data.property.similarities ?? []}
        isLoading={isLoading}
        isNotFound={!data.property.similarities?.length}
        total={data.property.similarities?.length}
        action={(item) => (
          <Button
            variant="soft"
            color="error"
            size="medium"
            disabled={isLoading}
            onClick={handleTrash(item)}
          >
            <Iconify icon="mdi:trash" />
          </Button>
        )}
      />

      <PropertyListDialog
        open={dialog.value}
        selected={(id) => selected.includes(id)}
        onClose={dialog.onFalse}
        onSelect={handleAdd}
      />
    </Stack>
  );
}
